import React from 'react';
import { parseMarkdown, splitDocument, countReviewMarkers } from '../lib/legalMarkdown';

/**
 * Draws a legal document (the privacy policy or the terms) from its markdown text.
 *
 * The text arrives from `src/lib/legal.generated.js`, which `scripts/gen_legal_pages.mjs`
 * copies from the `.md` files in the repository root. Nothing here is typed by a customer
 * and nothing here is rendered as HTML — `legalMarkdown` returns data, and every piece of
 * it becomes a React element below. A `<script>` in the source would appear on the page as
 * the literal characters.
 *
 * `preview` is the localhost-and-staging draft view: a red bar with the marker count, and every
 * `>>> … <<<` review marker highlighted so it cannot be mistaken for policy. The public
 * page never renders with `preview` — see pages/LegalPage.js for the two gates.
 *
 * Light-only, hive-* palette, like the landing page it sits inside (MarketingChrome).
 */

const plain = (tokens) => tokens.map((t) => (t.children ? plain(t.children) : (t.text || ''))).join('');

// "2. Who this policy is for" → "who-this-policy-is-for"
const slug = (tokens) => plain(tokens).toLowerCase().replace(/^\d+\.\s*/, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const Inline = ({ tokens }) => tokens.map((t, i) => {
  switch (t.type) {
    case 'bold': return <strong key={i} className="font-semibold text-hive-navy"><Inline tokens={t.children} /></strong>;
    case 'italic': return <em key={i}><Inline tokens={t.children} /></em>;
    case 'code': return <code key={i} className="font-mono text-[0.9em] bg-hive-cream rounded px-1">{t.text}</code>;
    case 'link': return (
      <a key={i} href={t.href} className="text-hive-orange hover:underline"
         {...(t.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}>
        {t.text}
      </a>
    );
    case 'marker': return (
      <mark key={i} className="bg-amber-100 text-inherit rounded px-1 outline-dashed outline-1 outline-amber-500"
            title="Not yet confirmed — must be resolved before this page goes live">
        <Inline tokens={t.children} />
      </mark>
    );
    default: return <React.Fragment key={i}>{t.text}</React.Fragment>;
  }
});

const Block = ({ block: b }) => {
  switch (b.type) {
    case 'heading': {
      if (b.level === 2) {
        return <h2 id={slug(b.children)} className="font-poppins text-xl font-bold text-hive-navy mt-10 mb-3 scroll-mt-6"><Inline tokens={b.children} /></h2>;
      }
      if (b.level === 3) return <h3 className="font-poppins text-lg font-semibold text-hive-navy mt-7 mb-2"><Inline tokens={b.children} /></h3>;
      return <h4 className="font-poppins font-semibold text-hive-navy mt-5 mb-1"><Inline tokens={b.children} /></h4>;
    }
    case 'paragraph': return <p className="mb-4 leading-relaxed"><Inline tokens={b.children} /></p>;
    case 'rule': return <hr className="border-hive-line my-7" />;
    case 'quote': return (
      <blockquote className="border-l-2 border-hive-orange bg-hive-cream text-hive-muted rounded-r-lg px-4 py-2 mb-4">
        {b.children.map((c, i) => <Block key={i} block={c} />)}
      </blockquote>
    );
    case 'list': {
      const Tag = b.ordered ? 'ol' : 'ul';
      return (
        <Tag className={`${b.ordered ? 'list-decimal' : 'list-disc'} pl-6 mb-4 space-y-1 leading-relaxed`}>
          {b.items.map((it, i) => <li key={i}><Inline tokens={it} /></li>)}
        </Tag>
      );
    }
    case 'table': return (
      <div className="overflow-x-auto mb-5">
        <table className="w-full text-[0.95rem] border-collapse">
          <thead>
            <tr>{b.header.map((c, i) => (
              <th key={i} className="text-left align-top font-semibold text-hive-muted text-sm py-2 px-2.5 border-b border-hive-line">
                <Inline tokens={c} />
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {b.rows.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => (
                <td key={ci} className="align-top py-2 px-2.5 border-b border-hive-line"><Inline tokens={c} /></td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    default: return null;
  }
};

const Contents = ({ blocks }) => {
  const sections = blocks.filter((b) => b.type === 'heading' && b.level === 2);
  if (sections.length < 3) return null;
  return (
    <nav aria-label="Contents" className="bg-hive-cream rounded-lg px-5 py-4 mb-8">
      <p className="font-poppins font-semibold text-hive-navy mb-2">Contents</p>
      <ol className="columns-1 sm:columns-2 gap-x-8 text-sm space-y-1">
        {sections.map((s, i) => (
          <li key={i}>
            <a href={`#${slug(s.children)}`} className="text-hive-orange hover:underline"><Inline tokens={s.children} /></a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

const LegalDocument = ({ text, preview = false }) => {
  const { title, updated, body } = splitDocument(text);
  const blocks = parseMarkdown(body);
  const markers = countReviewMarkers(text);
  return (
    <article className="text-hive-navy">
      {preview && (
        <div role="status" className="bg-[#C0392B] text-white font-semibold rounded-lg px-4 py-2.5 mb-6 text-sm">
          Draft preview — shown on your PC and on staging, never on the public site.
          {markers > 0
            ? ` ${markers} unresolved marker${markers === 1 ? '' : 's'} — the public page shows the holding page until they are gone.`
            : ' The text is clean; the public page shows it once LEGAL_PAGES_LIVE is switched on.'}
        </div>
      )}
      {title && <h1 className="font-poppins text-3xl font-bold text-hive-navy mb-1">{title}</h1>}
      {updated && <p className="text-hive-muted text-sm mb-6">Last updated: <Inline tokens={updated} /></p>}
      <Contents blocks={blocks} />
      {blocks.map((b, i) => <Block key={i} block={b} />)}
    </article>
  );
};

export default LegalDocument;
